import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faShieldAlt, faCheckCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import MonitoringReviewActions from '../review-actions'
import REVIEW_STYLES from './tokens'

const ReviewModal = ({
  icon, title, subtitle, badge, signals, legend, wide,
  analytic, rationale, footerMetric, transaction, review, onClose, onDecided,
  decidesGroup, children
}) => {
  const panelRef = useRef(null)

  // Hand-rolled overlay: focus trap, focus restore and scroll lock are ours to provide.
  useEffect(() => {
    const previouslyFocused = document.activeElement
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    panelRef.current?.focus()

    return () => {
      document.body.style.overflow = overflow
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus()
    }
  }, [])

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab' || !panelRef.current) return

      const focusable = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
      if (!focusable.length) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement

      if (event.shiftKey && (active === first || active === panelRef.current)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Cover every analytic on the row, or an ignored record would stay Open.
  const governingAnalytics = (transaction.analytics || [])
    .map((entry) => ({ id: entry.id, name: entry.name }))

  const whyFlagged = (
    // Above the 1100 overlay; a Bootstrap tooltip portals at 1080.
    <Tooltip className="tl-review__why" style={{ zIndex: 1200 }}>
      <div style={{ textAlign: 'left' }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '.08em',
            opacity: 0.75
          }}
        >
          Why flagged
        </div>
        {rationale && <div style={{ marginTop: 4 }}>{rationale}</div>}
        {signals.map((signal) => (
          <div key={signal} style={{ marginTop: 4, display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <FontAwesomeIcon icon={faCheckCircle} style={{ width: 10, height: 10, flex: 'none' }} />
            {signal}
          </div>
        ))}
      </div>
    </Tooltip>
  )

  const badgeOrHint = badge || (
    <span className="tl-review__hint">
      <FontAwesomeIcon icon={faInfoCircle} style={{ width: 12, height: 12 }} /> Why flagged
    </span>
  )

  return (
    <div
      className="tl-review"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 28
      }}
    >
      <style>{REVIEW_STYLES}</style>
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(15,24,41,.55)', backdropFilter: 'blur(2px)' }}
      />
      <section
        className="tl-review__panel"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tl-review-title"
        tabIndex={-1}
        style={{
          position: 'relative',
          width: wide ? 'min(1380px, 100%)' : 'min(1180px, 100%)',
          height: '100%',
          maxHeight: 824,
          background: 'var(--surface-page)',
          borderRadius: 'var(--radius-xl)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-xl)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            padding: '18px 24px',
            background: 'var(--surface-card)',
            borderBottom: '1px solid var(--border-subtle)'
          }}
        >
          <div
            style={{
              width: 42,
              height: 42,
              flex: 'none',
              borderRadius: 'var(--radius-md)',
              background: 'var(--navy-800)',
              color: 'var(--gold-500)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FontAwesomeIcon icon={icon} style={{ width: 18, height: 18 }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              id="tl-review-title"
              style={{
                fontSize: 'var(--text-2xl)',
                fontWeight: 800,
                color: 'var(--text-strong)',
                lineHeight: 1.1,
                margin: 0
              }}
            >
              {title}
            </h2>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: 2 }}>
              {subtitle}
            </div>
          </div>
          <OverlayTrigger placement="bottom" delay={{ show: 150, hide: 250 }} overlay={whyFlagged}>
            <span style={{ cursor: 'help' }} tabIndex={0} aria-label="Why flagged">
              {badgeOrHint}
            </span>
          </OverlayTrigger>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 38,
              height: 38,
              flex: 'none',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: 'var(--navy-50)',
              cursor: 'pointer',
              color: 'var(--navy-600)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FontAwesomeIcon icon={faTimes} style={{ width: 16, height: 16 }} />
          </button>
        </header>

        {/* Under the title, not a footer: a tall compare scrolls a footer out of view. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            rowGap: 12,
            flexWrap: 'wrap',
            padding: '12px 24px',
            background: 'var(--surface-card)',
            borderBottom: '1px solid var(--border-subtle)'
          }}
        >
          <FontAwesomeIcon icon={faShieldAlt} style={{ width: 16, height: 16, color: 'var(--rust-500)' }} />
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--text-strong)' }}>
            Flagged by {analytic.name}
          </div>
          {legend}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginLeft: 'auto' }}>
            {footerMetric}
            <MonitoringReviewActions
              transactionId={transaction.id}
              review={review}
              sectionId={transaction.section_id}
              sectionAbbreviation={transaction.section_abbreviation}
              vendorId={transaction.vendor_id}
              vendorName={transaction.vendor_name}
              employeeId={transaction.employee_id}
              employeeName={transaction.employee_full_name}
              controlAnalytics={governingAnalytics}
              allowEmailDraft={false}
              resolveWithoutReason
              duplicateAnalyticId={decidesGroup ? analytic.id : null}
              onChange={onDecided || onClose}
            />
          </div>
        </div>

        {children}

      </section>
    </div>
  )
}

ReviewModal.propTypes = {
  icon: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  badge: PropTypes.node,
  signals: PropTypes.arrayOf(PropTypes.string),
  legend: PropTypes.node,
  wide: PropTypes.bool,
  analytic: PropTypes.object.isRequired,
  rationale: PropTypes.node,
  footerMetric: PropTypes.node,
  transaction: PropTypes.object.isRequired,
  review: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  // Receives the new review; the modal just closes when absent.
  onDecided: PropTypes.func,
  // A duplicate is decided for every side at once; see useMonitoringReview.
  decidesGroup: PropTypes.bool,
  children: PropTypes.node
}

ReviewModal.defaultProps = { signals: [] }

export default ReviewModal
